import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Bell, CheckCircle2, Video, Play, X, Check, Trash2,
  Clock, AlertTriangle, Briefcase
} from 'lucide-react';
import {
  getNotifications, markNotificationRead, markAllNotificationsRead,
  clearAllNotifications
} from '../services/api';
import { useRegion } from '../context/RegionContext';
import { formatDistanceToNow } from 'date-fns';

const POLL_INTERVAL = 30000; // 30 seconds

const notificationIcons = {
  interview_completed: { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  interview_started: { icon: Play, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  issue_reported: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10' },
  ats_job_imported: { icon: Briefcase, color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
};

export const NotificationBell = ({ collapsed }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'read'
  const panelRef = useRef(null);
  const prevUnreadRef = useRef(0);
  const { region } = useRegion();

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getNotifications(100, false, region);
      setNotifications(data.notifications || []);
      const newUnread = data.unread_count || 0;

      // Browser notification for new completions
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current > 0) {
        const newest = (data.notifications || []).find(n => !n.read && n.type === 'interview_completed');
        if (newest && Notification.permission === 'granted') {
          new Notification('Interview Completed', {
            body: newest.message,
            icon: '/favicon.ico',
            tag: newest.id
          });
        }
      }

      prevUnreadRef.current = newUnread;
      setUnreadCount(newUnread);
    } catch (e) {
      // silent
    }
  }, []);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  // Initial load + polling
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkRead = async (id, e) => {
    e.stopPropagation();
    await markNotificationRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const handleClearAll = async () => {
    await clearAllNotifications();
    setNotifications([]);
    setUnreadCount(0);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markNotificationRead(notification.id);
      setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    if (notification.type === 'interview_completed' && notification.token) {
      const publicUrl = (process.env.REACT_APP_PUBLIC_URL || '').replace(/\/+$/, '') || window.location.origin;
      window.open(`${publicUrl}/interview/${notification.token}?recruiter=1`, '_blank');
    } else if (notification.job_id) {
      navigate(`/jobs/${notification.job_id}`);
    } else if (notification.type === 'ats_job_imported') {
      navigate('/jobs');
    }
    setIsOpen(false);
  };

  const timeAgo = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`relative ${collapsed ? 'justify-center px-0 w-full' : 'justify-start gap-3 w-full'}`}
        data-testid="notification-bell-btn"
      >
        <Bell className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />
        {!collapsed && <span className="text-sm">Notifications</span>}
        {unreadCount > 0 && (
          <span
            className={`absolute flex items-center justify-center h-[18px] min-w-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold ${
              collapsed ? 'top-0.5 right-1' : 'top-0.5 left-5'
            }`}
            data-testid="notification-unread-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Notification Panel */}
      {isOpen && (
        <div
          className={`absolute z-50 border bg-popover rounded-xl shadow-xl overflow-hidden ${
            collapsed ? 'left-[68px] bottom-0' : 'left-[240px] bottom-0'
          }`}
          style={{ width: 400, maxHeight: '80vh' }}
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Notifications
                </h3>
                {unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 h-5">
                    {unreadCount}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={handleMarkAllRead}
                    data-testid="mark-all-read-btn"
                  >
                    <Check className="h-3 w-3 mr-1" /> Read All
                  </Button>
                )}
                {notifications.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                    onClick={handleClearAll}
                    data-testid="clear-all-notifications-btn"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1 mt-2.5" data-testid="notification-filter-tabs">
              {[
                { key: 'all', label: 'All', count: notifications.length },
                { key: 'unread', label: 'Unread', count: notifications.filter(n => !n.read).length },
                { key: 'read', label: 'Read', count: notifications.filter(n => n.read).length },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    filter === tab.key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/60 text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid={`notification-tab-${tab.key}`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>
          </div>

          {/* Notification List - native scrollable div for reliable scrolling */}
          <div
            className="overflow-y-auto"
            style={{ maxHeight: 'calc(80vh - 120px)' }}
            data-testid="notification-list-scroll"
          >
            {filteredNotifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">
                  {filter === 'all' ? 'No notifications yet' : filter === 'unread' ? 'No unread notifications' : 'No read notifications'}
                </p>
                <p className="text-xs text-muted-foreground/60 mt-1">
                  {filter === 'all' ? "You'll be notified when candidates start or complete interviews" : 'Try switching to a different tab'}
                </p>
              </div>
            ) : (
              <div>
                {filteredNotifications.map((n) => {
                  const cfg = notificationIcons[n.type] || notificationIcons.interview_completed;
                  const IconComponent = cfg.icon;
                  return (
                    <div
                      key={n.id}
                      className={`flex gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 border-b border-border/50 ${
                        !n.read ? 'bg-indigo-500/5' : ''
                      }`}
                      onClick={() => handleNotificationClick(n)}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className={`h-9 w-9 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                        <IconComponent className={`h-4 w-4 ${cfg.color}`} strokeWidth={1.8} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium truncate ${!n.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {n.title}
                          </p>
                          {!n.read && (
                            <div className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {n.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
                            <Clock className="h-2.5 w-2.5" />
                            {timeAgo(n.created_at)}
                          </span>
                          {n.job_title && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                              {n.job_title}
                            </Badge>
                          )}
                          {n.score && n.type === 'interview_completed' && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                              Score: {n.score}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {!n.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 shrink-0 mt-1 text-muted-foreground hover:text-foreground"
                          onClick={(e) => handleMarkRead(n.id, e)}
                          data-testid={`mark-read-${n.id}`}
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
